import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Users,
  Monitor,
  Shield,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Plus,
  CreditCard,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import MSPLayout from '../components/MSPLayout';
import { getMSPDashboard, getMSPClients } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function MSPDashboard() {
  const navigate = useNavigate();
  const { msp } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, clientsRes] = await Promise.all([
        getMSPDashboard(),
        getMSPClients(),
      ]);
      setDashboard(dashboardRes.data);
      setClients(clientsRes.data.clients.slice(0, 5)); // Top 5 clients
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MSPLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </MSPLayout>
    );
  }

  const summary = dashboard?.summary || {};
  const threats = dashboard?.threats || {};

  return (
    <MSPLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">MSP Dashboard</h1>
            <p className="text-slate-400">Overview of your managed clients and earnings</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData} className="border-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => navigate('/msp/clients')} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
        </div>

        {/* Stripe Connect Banner */}
        {!msp?.stripe_connected && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-amber-500" />
                  <div>
                    <p className="text-white font-medium">Connect Stripe to receive commissions</p>
                    <p className="text-sm text-slate-400">
                      You will automatically receive 30% of client billings
                    </p>
                  </div>
                </div>
                <Button onClick={() => navigate('/msp/settings')} className="bg-amber-500 hover:bg-amber-600">
                  Connect Stripe
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Clients</p>
                  <p className="text-3xl font-bold text-white mt-1">{summary.total_clients || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className="text-slate-500">Managed companies</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Monthly Revenue</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    ${summary.mrr?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <TrendingUp className="w-4 h-4 text-emerald-500 mr-1" />
                <span className="text-emerald-500">
                  ${summary.monthly_commission?.toFixed(2) || 0} commission
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Protected Devices</p>
                  <p className="text-3xl font-bold text-white mt-1">{summary.total_devices || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <Users className="w-4 h-4 text-slate-500 mr-1" />
                <span className="text-slate-500">{summary.total_users || 0} users</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Threats Blocked</p>
                  <p className="text-3xl font-bold text-white mt-1">{threats.total_blocked || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                {summary.clients_at_risk > 0 ? (
                  <>
                    <AlertTriangle className="w-4 h-4 text-amber-500 mr-1" />
                    <span className="text-amber-500">{summary.clients_at_risk} clients at risk</span>
                  </>
                ) : (
                  <span className="text-emerald-500">All clients healthy</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Clients List */}
          <Card className="lg:col-span-2 bg-slate-900 border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Recent Clients</CardTitle>
                <CardDescription className="text-slate-400">
                  Your managed client companies
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/msp/clients')}
                className="border-slate-700"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No clients yet</p>
                  <Button 
                    onClick={() => navigate('/msp/clients')} 
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Client
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {clients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => navigate(`/msp/clients/${client.id}`)}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{client.company_name}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <span>{client.stats?.users_count || 0} users</span>
                            <span>•</span>
                            <span>{client.stats?.devices_count || 0} devices</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className={
                          client.stats?.health_status === 'critical'
                            ? 'bg-red-500/10 text-red-500'
                            : client.stats?.health_status === 'warning'
                            ? 'bg-amber-500/10 text-amber-500'
                            : 'bg-emerald-500/10 text-emerald-500'
                        }>
                          {client.stats?.health_status || 'healthy'}
                        </Badge>
                        <div className="text-right">
                          <p className="text-white font-medium">${client.billing?.monthly_amount || 0}</p>
                          <p className="text-xs text-slate-500">
                            +${client.billing?.commission?.toFixed(2) || 0} commission
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Earnings Summary */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Earnings Overview</CardTitle>
              <CardDescription className="text-slate-400">
                Your commission earnings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Commission Rate */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <p className="text-sm text-slate-400">Your Commission Rate</p>
                <p className="text-4xl font-bold text-white mt-1">30%</p>
                <p className="text-sm text-slate-400 mt-2">
                  Of all client subscription fees
                </p>
              </div>

              {/* Monthly Breakdown */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Client MRR</span>
                  <span className="text-white font-medium">
                    ${summary.mrr?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Your Commission (30%)</span>
                  <span className="text-emerald-400 font-medium">
                    +${summary.monthly_commission?.toFixed(2) || 0}
                  </span>
                </div>
                <hr className="border-slate-800" />
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Total Earnings</span>
                  <span className="text-white font-bold text-lg">
                    ${dashboard?.msp?.total_earnings?.toFixed(2) || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Pending Payout</span>
                  <span className="text-amber-400 font-medium">
                    ${dashboard?.msp?.pending_payout?.toFixed(2) || 0}
                  </span>
                </div>
              </div>

              <Button 
                onClick={() => navigate('/msp/earnings')} 
                variant="outline" 
                className="w-full border-slate-700"
              >
                View Earnings Details
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Threat Breakdown */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Threat Protection Summary</CardTitle>
            <CardDescription className="text-slate-400">
              Threats blocked across all your client companies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">DNS Threats Blocked</span>
                  <span className="text-white font-medium">{threats.dns_blocked || 0}</span>
                </div>
                <Progress 
                  value={threats.dns_blocked ? (threats.dns_blocked / (threats.total_blocked || 1)) * 100 : 0} 
                  className="h-2 bg-slate-800"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Files Quarantined</span>
                  <span className="text-white font-medium">{threats.files_quarantined || 0}</span>
                </div>
                <Progress 
                  value={threats.files_quarantined ? (threats.files_quarantined / (threats.total_blocked || 1)) * 100 : 0} 
                  className="h-2 bg-slate-800"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Emails Quarantined</span>
                  <span className="text-white font-medium">{threats.emails_quarantined || 0}</span>
                </div>
                <Progress 
                  value={threats.emails_quarantined ? (threats.emails_quarantined / (threats.total_blocked || 1)) * 100 : 0} 
                  className="h-2 bg-slate-800"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MSPLayout>
  );
}
